// M6 wave-2: line mode (team play) UI layer — typed fetch helpers + react-
// query hooks against the ALREADY-LIVE line-routes.ts endpoints (wave 1).
// Follows lib/engage.ts's conventions: `retry: false` on every read so a
// 404/offline/not-yet-configured job fails fast, and every consumer treats
// `data === undefined` as "no line UI for this job" rather than a loading
// spinner or an error banner — see that file's banner comment for the full
// feature-degrade contract. Design rule carried over verbatim from
// line-routes.ts: never surface a per-WORKER rate anywhere (the API doesn't
// even send one); per-station rates are fine, but ONLY in a manager-facing
// bottleneck view, never on the worker or TV surfaces.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { api, readJson } from "./api";

export type LineStation = { id: number; job_id: number; name: string; position: number; is_final: boolean };

export type LineWorker = { id: number; name: string };

export type LineStationState = {
  id: number; name: string; position: number; is_final: boolean;
  workers: LineWorker[]; station_gpm: number;
};

// Pooled piece-rate split by station-hours — money OWED, not a rate, so
// (unlike everything else here) it carries names and is shown to workers and
// managers alike. null without an org pay rate, absent from an older API.
export type LinePoolShare = { player_id: number; name: string; station_hours: number; cents: number };
export type LinePool = {
  rate_cents_per_g: number; currency: string; total_cents: number; shares: LinePoolShare[];
};

export type LineState = {
  job: { id: number; name: string; target_grams: number; total_grams: number; progress_pct: number };
  stations: LineStationState[];
  line_rate_gpm: number;
  eta_min: number | null;
  best_hour_g: number;
  current_hour_g: number;
  bottleneck_station_id: number | null;
  pool?: LinePool | null;
};

export function lineStationsKey(jobId: number | null): readonly [string, number | null] {
  return ["line-stations", jobId] as const;
}

/** GET /api/line/jobs/:jobId/stations — station lineup in position order.
 * Disabled (and silently absent, per the file banner) with no jobId. */
export function useLineStations(jobId: number | null): UseQueryResult<LineStation[]> {
  return useQuery({
    queryKey: lineStationsKey(jobId),
    queryFn: () => api.get(`/api/line/jobs/${jobId}/stations`).then((r) => readJson<LineStation[]>(r)),
    enabled: jobId !== null,
    retry: false,
  });
}

/** POST /api/line/jobs/:jobId/stations — manager-only replace-all. Caller is
 * responsible for the "exactly one is_final" invariant (enforced client-side
 * in the editor UI, not re-validated here). */
export async function saveLineStations(
  jobId: number, stations: { name: string; position: number; is_final: boolean }[],
): Promise<LineStation[]> {
  const res = await api.postJson(`/api/line/jobs/${jobId}/stations`, stations);
  return readJson<LineStation[]>(res);
}

/** GET /api/line/state?job_id= — the shared room number (line rate + ETA +
 * best-hour record) plus the manager-only per-station breakdown. Polling is
 * the caller's responsibility via `refetchInterval` (dashboard/TV both want
 * it only while their panel is actually open/visible). */
export function useLineState(
  jobId: number | null, opts?: { enabled?: boolean; refetchInterval?: number | false },
): UseQueryResult<LineState> {
  return useQuery({
    queryKey: ["line-state", jobId],
    queryFn: () => api.get(`/api/line/state?job_id=${jobId}`).then((r) => readJson<LineState>(r)),
    enabled: jobId !== null && (opts?.enabled ?? true),
    refetchInterval: opts?.refetchInterval ?? false,
    retry: false,
  });
}

/** Best-effort single fetch (not a hook) — used by the TV board to scan
 * several open jobs for the first one that qualifies as a live line, where a
 * per-job useQuery would be awkward (unknown, changing job count). Never
 * throws: a failed/404 lookup is just "this job isn't a line" to the caller. */
export async function fetchLineState(jobId: number): Promise<LineState | null> {
  try {
    const res = await api.get(`/api/line/state?job_id=${jobId}`);
    if (!res.ok) return null;
    return await readJson<LineState>(res);
  } catch {
    return null;
  }
}

/** Finds the first job (in the given order) that qualifies for the TV
 * board's Line pane: it has stations configured AND at least one is
 * currently staffed. Returns null if none qualify — the pane is then simply
 * absent, same silent-degrade contract as the rest of this file. */
export async function findActiveLine(jobIds: number[]): Promise<LineState | null> {
  const results = await Promise.all(jobIds.map((id) => fetchLineState(id)));
  for (const state of results) {
    if (state && state.stations.length > 0 && state.stations.some((s) => s.workers.length > 0)) return state;
  }
  return null;
}

export type ClockInResult = { id: number; station_id: number; job_id: number; clock_in_ms: number };

/** POST /api/line/clockin — server auto-clocks-out any other open clock-in
 * for this player first, so switching stations (or jobs) is just calling
 * this again with the new station_id. Throws (via readJson) on failure —
 * callers show a toast, same convention as lib/engage.ts's setEngageTarget. */
export async function clockIn(jobId: number, stationId: number): Promise<ClockInResult> {
  const res = await api.postJson("/api/line/clockin", { job_id: jobId, station_id: stationId });
  return readJson<ClockInResult>(res);
}

/** POST /api/line/clockout. Safe to call speculatively ("did this player
 * leave a station open somewhere?") — a 400 "not clocked in" is swallowed
 * and reported as `false` rather than thrown, since every call site here
 * uses it as a best-effort cleanup, not a user-facing action with its own
 * error state. */
export async function clockOut(): Promise<boolean> {
  try {
    const res = await api.postJson("/api/line/clockout", {});
    return res.ok;
  } catch {
    return false;
  }
}
